import type { CallHistoryFilters } from '@/types/calls';
import type { NominalsFilters, UsersFilters } from '@/types/nominals';
import type { DetectedKeywordHistoryFilters, MonitoredWordsFilters } from '@/types/keywords';

type PaginationFilters = {
  page?: number;
  page_size?: number;
};

export function appendPaginationParams(
  params: URLSearchParams,
  filters: PaginationFilters
): void {
  if (filters.page) {
    params.append('page', filters.page.toString());
  }
  if (filters.page_size) {
    params.append('page_size', filters.page_size.toString());
  }
}

export function appendCallHistoryFilterParams(
  params: URLSearchParams,
  filters: CallHistoryFilters
): void {
  if (filters.search) {
    params.append('search', filters.search);
  }
  if (filters.hangup_cause) {
    params.append('hangup_cause', filters.hangup_cause);
  }
  if (filters.start_date) {
    params.append('start_date', `${filters.start_date}T00:00:00`);
  }
  if (filters.end_date) {
    params.append('end_date', `${filters.end_date}T23:59:59`);
  }
  if (filters.is_video !== undefined) {
    params.append('is_video', filters.is_video.toString());
  }
  if (filters.min_duration !== undefined) {
    params.append('min_duration', filters.min_duration.toString());
  }
  if (filters.max_duration !== undefined) {
    params.append('max_duration', filters.max_duration.toString());
  }
  if (filters.establishment_id !== undefined) {
    params.append('establishment_id', filters.establishment_id.toString());
  }
}

export function buildCallHistoryQueryString(
  filters: CallHistoryFilters,
  extraParams: Record<string, string> = {}
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(extraParams)) {
    params.append(key, value);
  }
  appendPaginationParams(params, filters);
  appendCallHistoryFilterParams(params, filters);
  return params.toString();
}

export function buildNominalsQueryString(filters: NominalsFilters = {}): string {
  const params = new URLSearchParams();
  appendPaginationParams(params, filters);
  if (filters.search) {
    params.append('search', filters.search);
  }
  if (filters.is_active !== undefined) {
    params.append('is_active', filters.is_active.toString());
  }
  if (filters.establishment_id !== undefined) {
    params.append('establishment', filters.establishment_id.toString());
  }
  return params.toString();
}

export function buildUsersQueryString(filters: UsersFilters = {}): string {
  const params = new URLSearchParams();
  appendPaginationParams(params, filters);
  if (filters.search) {
    params.append('search', filters.search);
  }
  if (filters.is_active !== undefined) {
    params.append('is_active', String(filters.is_active));
  }
  if (filters.role) {
    params.append('role', filters.role);
  }
  if (filters.department) {
    params.append('department', filters.department);
  }
  if (filters.establishment !== undefined) {
    params.append('establishment', filters.establishment.toString());
  }
  return params.toString();
}

export function buildMonitoredWordsQueryString(
  filters: MonitoredWordsFilters = {}
): string {
  const params = new URLSearchParams();
  appendPaginationParams(params, filters);
  if (filters.search) {
    params.append('search', filters.search);
  }
  if (filters.is_active !== undefined) {
    params.append('is_active', filters.is_active.toString());
  }
  if (filters.auto_terminate !== undefined) {
    params.append('auto_terminate', filters.auto_terminate.toString());
  }
  return params.toString();
}

export function buildDetectedKeywordHistoryQueryString(
  filters: DetectedKeywordHistoryFilters = {}
): string {
  const params = new URLSearchParams();
  appendPaginationParams(params, filters);
  if (filters.search) {
    params.append('search', filters.search);
  }
  if (filters.start_date) {
    params.append('detected_at_start', `${filters.start_date}T00:00:00`);
  }
  if (filters.end_date) {
    params.append('detected_at_end', `${filters.end_date}T23:59:59`);
  }
  if (filters.call_terminated !== undefined) {
    params.append('call_terminated', filters.call_terminated.toString());
  }
  return params.toString();
}

export function buildPaginatedPath(basePath: string, queryString: string): string {
  return queryString ? `${basePath}?${queryString}` : basePath;
}
